import { subscribeRegister } from "../../services/mqttClient.ts";
import { groupByDictionaryKey } from "../../services/registerUtils.ts";
import {
  applySummaryThresholdToElement,
  getSummaryThresholdSeverity,
  resolveFieldThresholdMeta,
} from "../../services/summaryThreshold.ts";
import Utils from "../../framework/utils.ts";

const THRESHOLD_FIELD = '__visual_metric__';

function normalizeAggregateMode(mode) {
  if (mode === 'sum') return 'sum';
  if (mode === 'average') return 'avg';
  return 'avg';
}

function buildVisualThresholdSpec(cfg) {
  return {
    [THRESHOLD_FIELD]: {
      keys: [String(cfg?.configKey || '').toUpperCase()],
      mode: normalizeAggregateMode(cfg?.mode),
    },
  };
}

function severityToColor(severity) {
  if (severity === 'critical') return '#7f1d1d';
  if (severity === 'high') return '#d32f2f';
  return '#2e7d32';
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function toNumber(value, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function gaugeArcLength() {
  return 376;
}

function buildGaugeMarkup({ label, value, unit, min, max, color, darkMode }) {
  const safeMin = toNumber(min, 0);
  const safeMax = toNumber(max, 100);
  const range = safeMax - safeMin || 1;
  const clamped = clamp(toNumber(value, safeMin), safeMin, safeMax);
  const percent = clamp((clamped - safeMin) / range, 0, 1);
  const dash = percent * gaugeArcLength();
  const textColor = darkMode ? '#f8fafc' : '#0f172a';
  const mutedColor = darkMode ? '#94a3b8' : '#475569';
  const trackColor = darkMode ? '#1f2937' : '#cbd5e1';

  return `
    <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;">
      <svg width="100%" height="180" viewBox="0 0 280 180" style="max-width:280px;display:block;margin:0 auto;overflow:visible;">
        <path d="M20 160 A120 120 0 0 1 260 160" fill="none" stroke="${trackColor}" stroke-width="28" />
        <path d="M20 160 A120 120 0 0 1 260 160" fill="none" stroke="${color}" stroke-width="20" stroke-dasharray="${dash} ${gaugeArcLength()}" />
        <text x="20" y="174" text-anchor="start" font-family="Inter, sans-serif" font-size="11" fill="${mutedColor}">${safeMin.toFixed(0)}</text>
        <text x="260" y="174" text-anchor="end" font-family="Inter, sans-serif" font-size="11" fill="${mutedColor}">${safeMax.toFixed(0)}</text>
        <text x="140" y="96" text-anchor="middle" font-family="Inter, sans-serif" font-size="30" font-weight="700" fill="${color}">${clamped.toFixed(1)}</text>
        <text x="140" y="118" text-anchor="middle" font-family="Inter, sans-serif" font-size="12" fill="${mutedColor}">${unit || ''}</text>
        <text x="140" y="142" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" fill="${textColor}">${label}</text>
      </svg>
    </div>
  `;
}

function buildMeterMarkup({ label, value, unit, min, max, color, darkMode }) {
  const safeMin = toNumber(min, 0);
  const safeMax = toNumber(max, 100);
  const range = safeMax - safeMin || 1;
  const clamped = clamp(toNumber(value, safeMin), safeMin, safeMax);
  const percent = clamp(((clamped - safeMin) / range) * 100, 0, 100);
  const textColor = darkMode ? '#f8fafc' : '#0f172a';
  const mutedColor = darkMode ? '#94a3b8' : '#475569';
  const trackColor = darkMode ? '#1f2937' : '#cbd5e1';

  return `
    <div style="display:flex;flex-direction:column;gap:0.5rem;justify-content:center;height:100%;padding:0.5rem 0;">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:0.75rem;">
        <span style="font-size:0.85rem;color:${textColor};font-weight:600;">${label}</span>
        <span style="font-size:1.1rem;color:${color};font-weight:700;">${clamped.toFixed(2)}${unit ? ` ${unit}` : ''}</span>
      </div>
      <div style="height:12px;border-radius:999px;background:${trackColor};overflow:hidden;">
        <div style="height:100%;width:${percent}%;background:${color};transition:width 0.35s ease;"></div>
      </div>
      <div style="display:flex;justify-content:space-between;font-size:0.75rem;color:${mutedColor};">
        <span>${safeMin.toFixed(0)}</span>
        <span>${safeMax.toFixed(0)}</span>
      </div>
    </div>
  `;
}

export class GaugeMeterManager {
  constructor(site, registers) {
    this.site = site;
    this.registers = registers;
    this.subscriptions = [];
    this.activeVisuals = new Map(); // containerId -> info

    // Detect current theme using Utils
    this.darkMode = Utils.getCurrentTheme() === "dark";

    // Listen for theme changes
    window.addEventListener("themechange", (e) => {
      this.setDarkMode(e.detail.theme === "dark");
    });
  }

  setDarkMode(enabled) {
    this.darkMode = !!enabled;
    this.activeVisuals.forEach((visual) => {
      if (visual.type === 'gauge') this._renderGauge(visual);
      if (visual.type === 'meter') this._renderMeter(visual);
    });
  }

  createVisual(containerId, type, config = []) {
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`[GaugeMeterManager] Missing container: ${containerId}`);
      return;
    }
    if (!Array.isArray(config) || config.length === 0) {
      console.warn(`[GaugeMeterManager] Empty config for ${containerId}`);
      return;
    }

    if (type === 'gauge') this._initGauge(containerId, container, config);
    else if (type === 'meter') this._initMeter(containerId, container, config);
    else if (type === 'indicator') this._initIndicator(containerId, container, config);
  }

  _initGauge(containerId, container, config) {
    const cfg = config[0];
    const spec = buildVisualThresholdSpec(cfg);
    const meta = resolveFieldThresholdMeta(THRESHOLD_FIELD, this.registers, spec) || {};
    const baseRange = [meta.engineeringMin ?? meta.alarmLowLow ?? meta.alarmLow ?? 0, meta.engineeringMax ?? meta.alarmHighHigh ?? meta.alarmHigh ?? 100];
    const visual = {
      id: containerId,
      type: 'gauge',
      container,
      config,
      meta,
      min: baseRange[0],
      max: baseRange[1],
      dynamicMin: baseRange[0],
      dynamicMax: baseRange[1],
      value: null,
    };

    this.activeVisuals.set(containerId, visual);
    this._renderGauge(visual);

    this._subscribeAndAggregate(config, value => {
      const severity = getSummaryThresholdSeverity(THRESHOLD_FIELD, value, this.registers, spec);
      if (cfg.adaptive) {
        if (value < visual.dynamicMin) visual.dynamicMin = value;
        if (value > visual.dynamicMax) visual.dynamicMax = value;
      }

      visual.value = value;
      visual.color = severityToColor(severity);
      this._renderGauge(visual);
    });
  }

  _initMeter(containerId, container, config) {
    const cfg = config[0];
    const spec = buildVisualThresholdSpec(cfg);
    const meta = resolveFieldThresholdMeta(THRESHOLD_FIELD, this.registers, spec) || {};
    const baseRange = [meta.engineeringMin ?? meta.alarmLowLow ?? meta.alarmLow ?? 0, meta.engineeringMax ?? meta.alarmHighHigh ?? meta.alarmHigh ?? 100];

    const visual = {
      id: containerId,
      type: 'meter',
      container,
      config,
      meta,
      min: baseRange[0],
      max: baseRange[1],
      value: null,
    };

    this.activeVisuals.set(containerId, visual);
    this._renderMeter(visual);

    this._subscribeAndAggregate(config, value => {
      const severity = getSummaryThresholdSeverity(THRESHOLD_FIELD, value, this.registers, spec);
      visual.value = value;
      visual.color = severityToColor(severity);
      this._renderMeter(visual);
    });
  }

  _initIndicator(containerId, container, config) {
    const cfg = config[0];
    const spec = buildVisualThresholdSpec(cfg);

    const visual = { id: containerId, type: 'indicator', container, config };
    this.activeVisuals.set(containerId, visual);

    container.innerHTML = `
      <div class="indicator">
        <div class="light off"></div>
        <span>${config[0].name || "Indicator"}</span>
      </div>
    `;
    const light = container.querySelector(".light") as HTMLElement;

    this._subscribeAndAggregate(config, value => {
      light.className = "light " + (value ? "on" : "off");
      applySummaryThresholdToElement(light, THRESHOLD_FIELD, Number(value || 0), this.registers, spec);
    });
  }

  _renderGauge(visual) {
    const cfg = visual.config?.[0] || {};
    visual.container.innerHTML = buildGaugeMarkup({
      label: cfg.name || cfg.configKey || 'Gauge',
      value: visual.value ?? visual.min,
      unit: cfg.unit || '',
      min: visual.dynamicMin ?? visual.min,
      max: visual.dynamicMax ?? visual.max,
      color: visual.color || '#2e7d32',
      darkMode: this.darkMode,
    });
  }

  _renderMeter(visual) {
    const cfg = visual.config?.[0] || {};
    visual.container.innerHTML = buildMeterMarkup({
      label: cfg.name || cfg.configKey || 'Meter',
      value: visual.value ?? visual.min,
      unit: cfg.unit || '',
      min: visual.min,
      max: visual.max,
      color: visual.color || '#2e7d32',
      darkMode: this.darkMode,
    });
  }

  _subscribeAndAggregate(config, callback) {
    const cfg = config?.[0];
    if (!cfg) return;
    const groups = groupByDictionaryKey(this.registers);
    const group = groups[String(cfg.configKey || '').toLowerCase()] || [];
    if (!group.length) return;

    const values = {};

    const computeAggregate = () => {
      const vals = Object.values(values).filter(v => v != null);
      if (!vals.length) return null;
      switch (cfg.mode) {
        case "sum": return vals.reduce((a, b) => a + b, 0);
        case "average": return vals.reduce((a, b) => a + b, 0) / vals.length;
        case "min": return Math.min(...vals);
        case "max": return Math.max(...vals);
        default: return vals[0];
      }
    };

    // Helper to trigger update if all needed data is available
    const tryUpdate = () => {
      const aggregated = computeAggregate();
      if (aggregated != null) {
        callback(aggregated);
      }
    };

    group.forEach(g => {
      const reg = this.registers.find(r => r.name === g.name);
      if (!reg) return;

      // Use current cached value immediately if available
      if (reg.value != null) {
        values[reg._id] = reg.value;
      }

      // Now subscribe for future updates
      const unsub = subscribeRegister(this.site._id, reg.deviceId, reg._id, payload => {
        const raw = payload?.value ?? null;
        values[reg._id] = raw != null ? Number(raw) : null;
        tryUpdate();
      });

      this.subscriptions.push(unsub);
    });

    // After setting initial values from cache, trigger first render immediately
    tryUpdate();
  }
  cleanup() {
    this.subscriptions.forEach(u => typeof u === "function" && u());
    this.subscriptions = [];
    this.activeVisuals.clear();
  }
}
